// shared/pdf/documents/ReceiptDocument.jsx
import React from 'react';
import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import { PDF_THEMES, DEFAULT_THEME } from '../themes';
import PdfHeader from '../components/PdfHeader';
import PdfFooter from '../components/PdfFooter';
import PdfSignatureBlock from '../components/PdfSignatureBlock';
import { fmtINR, fmtDate, cap } from '../pdfUtils';

const s = StyleSheet.create({
  page:     { fontFamily: 'NotoSans', paddingBottom: 60 },
  metaRow:  { flexDirection: 'row', gap: 10, marginHorizontal: 40, marginTop: 14, marginBottom: 14 },
  metaBox:  { flex: 1, padding: 10, borderRadius: 4 },
  metaLbl:  { fontSize: 7, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 },
  metaVal:  { fontSize: 12, fontWeight: 'bold' },
  colsWrap: { flexDirection: 'row', marginHorizontal: 40, gap: 14 },
  col:      { flex: 1 },
  secBar:   { padding: 5, marginBottom: 8, borderRadius: 3 },
  secLabel: { fontSize: 7.5, fontWeight: 'bold', color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: 0.5 },
  kv:       { flexDirection: 'row', marginBottom: 6 },
  kvLabel:  { fontSize: 8, width: 80 },
  kvValue:  { fontSize: 9, flex: 1 },
  amtWrap:  { marginHorizontal: 40, marginTop: 18, padding: 14, borderRadius: 6, alignItems: 'center' },
  amtLbl:   { fontSize: 9, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 },
  amtVal:   { fontSize: 22, fontWeight: 'bold' },
});

export default function ReceiptDocument({ data, theme: themeKey, orientation, sections }) {
  const t = PDF_THEMES[themeKey] || PDF_THEMES[DEFAULT_THEME];
  const { receipt, derasar, member } = data;

  const isGold = receipt.receiptType === 'gold';
  const isAnon = receipt.receiptType === 'anonymous' || !member;

  return (
    <Document>
      <Page size="A4" orientation={orientation || 'portrait'} style={[s.page, { backgroundColor: t.white }]}>
        {sections.header && <PdfHeader derasar={derasar} reportTitle="Donation Receipt" theme={t} />}

        <View style={s.metaRow}>
          {[
            { label: 'Receipt No.',    value: receipt.receiptNo || '—' },
            { label: 'Date',           value: fmtDate(receipt.receiptDate) },
            { label: 'Financial Year', value: receipt.financialYearId?.yearLabel || '—' },
          ].map((item, i) => (
            <View key={i} style={[s.metaBox, { backgroundColor: t.light }]}>
              <Text style={[s.metaLbl, { color: t.muted }]}>{item.label}</Text>
              <Text style={[s.metaVal, { color: t.body }]}>{item.value}</Text>
            </View>
          ))}
        </View>

        <View style={s.colsWrap}>
          <View style={s.col}>
            <View style={[s.secBar, { backgroundColor: t.primary }]}>
              <Text style={s.secLabel}>Donor Information</Text>
            </View>
            {isAnon ? (
              <Text style={{ fontSize: 10, color: t.muted }}>Anonymous Donor</Text>
            ) : (
              <>
                <Text style={{ fontSize: 11, fontWeight: 'bold', color: t.body, marginBottom: 4 }}>{member.name}</Text>
                {member.email && <Text style={{ fontSize: 8.5, color: t.muted, marginBottom: 2 }}>{member.email}</Text>}
                {member.phone && <Text style={{ fontSize: 8.5, color: t.muted }}>{member.phone}</Text>}
              </>
            )}
          </View>

          <View style={s.col}>
            <View style={[s.secBar, { backgroundColor: t.primary }]}>
              <Text style={s.secLabel}>Donation Details</Text>
            </View>
            {[
              { label: 'Type',     value: cap(receipt.receiptType) },
              { label: 'Mode',     value: cap(receipt.paymentMode) },
              { label: 'Category', value: receipt.donationCategory?.name || (receipt.offeringIds?.length ? `${receipt.offeringIds.length} categories` : '—') },
              receipt.referenceNo ? { label: receipt.paymentMode === 'cheque' ? 'Cheque No' : receipt.paymentMode === 'upi' ? 'UPI Ref No' : 'Bank Ref No', value: receipt.referenceNo } : null,
              receipt.narration ? { label: 'Note', value: receipt.narration } : null,
            ].filter(Boolean).map((kv, i) => (
              <View key={i} style={s.kv}>
                <Text style={[s.kvLabel, { color: t.muted }]}>{kv.label}</Text>
                <Text style={[s.kvValue, { color: t.body }]}>{kv.value}</Text>
              </View>
            ))}
          </View>
        </View>

        {isGold && receipt.goldGrams && (
          <View style={{ flexDirection: 'row', marginHorizontal: 40, marginTop: 12, gap: 10 }}>
            {[
              { label: 'Gold (g)', value: `${receipt.goldGrams}g` },
              receipt.goldPurity ? { label: 'Purity', value: `${receipt.goldPurity}K` } : null,
              receipt.valuationRate ? { label: 'Rate/g', value: fmtINR(receipt.valuationRate) } : null,
            ].filter(Boolean).map((item, i) => (
              <View key={i} style={[s.metaBox, { backgroundColor: t.light, flex: 0, paddingHorizontal: 14 }]}>
                <Text style={[s.metaLbl, { color: t.muted }]}>{item.label}</Text>
                <Text style={[s.metaVal, { color: t.body, fontSize: 10 }]}>{item.value}</Text>
              </View>
            ))}
          </View>
        )}

        <View style={[s.amtWrap, { backgroundColor: t.light, borderWidth: 1.5, borderColor: t.mid }]}>
          <Text style={[s.amtLbl, { color: t.muted }]}>Amount Received</Text>
          <Text style={[s.amtVal, { color: t.primary }]}>{fmtINR(receipt.amountInr)}</Text>
        </View>

        {sections.signature && <PdfSignatureBlock theme={t} />}
        {sections.footer    && <PdfFooter theme={t} />}
      </Page>
    </Document>
  );
}


